import React, { useEffect, useState } from "react";
import { Loader2, Search, UserCheck, RefreshCw } from "lucide-react";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";

// Pulls the (name, id) list from the external admission system
// (api-panel.mindcoders.in/admission — fetched server-side by our own
// backend, so no CORS issues here) and lets you mark today's attendance
// for a matching student in our system, straight from this list — no
// need to know which batch they're in or dig through the roster.
const ExternalAdmissionsPage = () => {
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [markingName, setMarkingName] = useState(null);
  const [results, setResults] = useState({}); // { [name]: { status: 'ok'|'error', message } }
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(apiUrl.EXTERNAL_ADMISSIONS);
      if (res.data?.status && Array.isArray(res.data?.data)) {
        setAdmissions(res.data.data);
      } else {
        setAdmissions([]);
        setError(res.data?.message || "Failed to load external admissions");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to reach external admission API"
      );
      setAdmissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Load batches too, so the admin can pick which batch to mark
    // attendance against — matters when the matched student doesn't
    // already have a batch on their enrollment, or you want to override it.
    const loadBatches = async () => {
      try {
        const res = await axiosInstance.get(apiUrl.GET_BATCHES);
        if (res.data?.status && Array.isArray(res.data?.data)) {
          setBatches(res.data.data);
        }
      } catch (err) {
        // Non-fatal — batch selection is optional, attendance can still
        // be marked using whatever batch the student's enrollment has.
      }
    };
    loadBatches();
  }, []);

  const handleMark = async (name, status) => {
    setMarkingName(name);
    try {
      const res = await axiosInstance.post(apiUrl.MARK_ATTENDANCE_BY_NAME, {
        name,
        status,
        batchId: selectedBatch || undefined,
      });
      setResults((prev) => ({
        ...prev,
        [name]: {
          ok: !!res.data?.status,
          message: res.data?.message,
        },
      }));
    } catch (err) {
      setResults((prev) => ({
        ...prev,
        [name]: {
          ok: false,
          message: err.response?.data?.message || "Failed to mark attendance",
        },
      }));
    } finally {
      setMarkingName(null);
    }
  };

  const filtered = admissions.filter((a) =>
    (a.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-200">
            External Admissions
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            From api-panel.mindcoders.in/admission — name + id only. Mark
            today's attendance for a matching student in our system directly
            from here.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-[#1d3350] bg-[#0f1c2e] px-3 py-2 text-sm font-medium text-slate-300 hover:bg-[#13233a]"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name..."
            className="w-full pl-9 pr-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <select
            value={selectedBatch}
            onChange={(e) => setSelectedBatch(e.target.value)}
            className="px-3 py-2 bg-[#0d1220] text-slate-100 border border-[#1d3350] rounded-lg text-sm outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Batch: use student's existing batch</option>
            {batches.map((b) => (
              <option key={b._id} value={b._id}>
                {b.batchName}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-slate-500 mt-1">
            Pick a batch to assign it (or override it) when marking below
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-4 text-sm text-red-400">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center text-sm text-slate-500">
          No admission records found.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[#1d3350]">
          <table className="w-full text-sm">
            <thead className="bg-[#0f1c2e] text-slate-400">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">Name</th>
                <th className="px-4 py-2.5 text-left font-medium">External ID</th>
                <th className="px-4 py-2.5 text-left font-medium">Mark Attendance</th>
                <th className="px-4 py-2.5 text-left font-medium">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1d3350]">
              {filtered.map((a) => {
                const result = results[a.name];
                return (
                  <tr key={a.id || a.name} className="bg-[#0d1b2e]">
                    <td className="px-4 py-2.5 text-slate-200 capitalize">{a.name}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{a.id}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={markingName === a.name}
                          onClick={() => handleMark(a.name, "present")}
                          className="inline-flex items-center gap-1 rounded bg-green-600/20 text-green-400 border border-green-600/40 px-2.5 py-1 text-xs font-medium hover:bg-green-600/30 disabled:opacity-50"
                        >
                          {markingName === a.name ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <UserCheck size={12} />
                          )}
                          Present
                        </button>
                        <button
                          type="button"
                          disabled={markingName === a.name}
                          onClick={() => handleMark(a.name, "absent")}
                          className="inline-flex items-center gap-1 rounded bg-red-600/20 text-red-400 border border-red-600/40 px-2.5 py-1 text-xs font-medium hover:bg-red-600/30 disabled:opacity-50"
                        >
                          Absent
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {result && (
                        <span className={result.ok ? "text-green-400" : "text-amber-400"}>
                          {result.message}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExternalAdmissionsPage;